// The 4:5 card crop. This produces `minithumbnailUrl`, which is what the home
// page grid actually renders -- a video with a poster but no crop shows no
// artwork at all there, because Home.js hides the image on error.
//
// It used to take the whole video map plus Admin's raw state setters and index
// into `videoData[identifier]` without a guard, which crashed the card for any
// video not yet in the map. It now takes one record and one callback, matching
// every sibling.

import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Box, Button, Slider, Typography } from '@mui/material';
import { describeError, patchVideo, uploadThumbnail } from '../admin/api';

async function toCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageSrc;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  canvas
    .getContext('2d')
    .drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))),
      'image/jpeg',
      0.9,
    );
  });
}

const ImageCropper = ({ video, identifier, onUpdateVideoData, onNotify = () => {} }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [editing, setEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const sourceUrl = video?.thumbnailUrl || '';
  const existing = video?.minithumbnailUrl || '';
  const [displayImageUrl, setDisplayImageUrl] = useState(sourceUrl);

  useEffect(() => {
    if (!selectedFile) {
      setDisplayImageUrl(sourceUrl);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setDisplayImageUrl(e.target.result);
      setEditing(true);
    };
    reader.readAsDataURL(selectedFile);
  }, [selectedFile, sourceUrl]);

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const cropAndUpload = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await toCroppedBlob(displayImageUrl, croppedAreaPixels);
      const url = await uploadThumbnail(identifier, blob, 'cropped.jpg');
      // Two steps on purpose: the upload returns a URL, and only then does the
      // record learn about it. A failure between them leaves an orphan image
      // rather than a record pointing at nothing.
      onUpdateVideoData(await patchVideo(identifier, { minithumbnailUrl: url }));
      onNotify('Card crop updated.', 'success');
      setEditing(false);
      setSelectedFile(null);
    } catch (error) {
      onNotify(describeError(error, 'The crop was not saved.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (existing && !editing) {
    return (
      <Box>
        <img
          src={existing}
          style={{
            width: 160,
            borderRadius: 'var(--ps-r-chip)',
            border: '1px solid var(--ps-line-subtle)',
            display: 'block',
            marginBottom: 10,
          }}
          alt="Card crop"
        />
        <Button onClick={() => setEditing(true)}>Recrop</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button component="label" sx={{ mb: '10px' }}>
        Use a different image
        <input
          hidden
          type="file"
          accept="image/*"
          aria-label="Card crop source image"
          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
        />
      </Button>

      {displayImageUrl ? (
        <>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 380,
              borderRadius: 'var(--ps-r-chip)',
              overflow: 'hidden',
              border: '1px solid var(--ps-line-subtle)',
              background: 'var(--ps-well)',
            }}
          >
            <Cropper
              image={displayImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={4 / 5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px', mt: '10px' }}>
            <span className="admin-label">Zoom</span>
            <Slider
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(_, v) => setZoom(v)}
              sx={{ maxWidth: 200 }}
            />
            <Box sx={{ flex: 1 }} />
            {existing && <Button onClick={() => setEditing(false)}>Cancel</Button>}
            <Button variant="contained" onClick={cropAndUpload} disabled={busy || !croppedAreaPixels}>
              {busy ? 'Uploading…' : 'Save crop'}
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'var(--ps-text-dim)' }}>
          Upload a poster image first, or choose a file above to crop from.
        </Typography>
      )}
    </Box>
  );
};

export default ImageCropper;
